/** Reading & display settings: typeface, size, spacing, colours, motion, contrast, language. */
import { LOCALES, type LocaleTag } from "@shared/i18n";
import { useI18n, useT } from "../i18n";
import {
  type ContrastChoice,
  type FontChoice,
  type MotionChoice,
  resetPrefs,
  type SizeChoice,
  type SpacingChoice,
  type ThemeChoice,
  usePrefs,
} from "../prefs";
import { Button, Dialog, Segmented } from "./ui";

export function LanguageSelect({ compact }: { compact?: boolean }) {
  const { locale, setLocale, t } = useI18n();
  return (
    <label class={compact ? "row" : "field"}>
      <span class={compact ? "visually-hidden" : "label"}>{t("a11y.language")}</span>
      <select
        class="input"
        style={compact ? { minHeight: 40, padding: "6px 10px", width: "auto" } : undefined}
        value={locale}
        onChange={(e) => setLocale((e.currentTarget as HTMLSelectElement).value as LocaleTag)}
      >
        {LOCALES.map((l) => (
          <option key={l.tag} value={l.tag} lang={l.tag}>
            {l.name}
          </option>
        ))}
      </select>
    </label>
  );
}

export function A11yPanel({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [prefs, setPrefs] = usePrefs();
  const fonts: { value: FontChoice; label: string; help: string }[] = [
    { value: "atkinson", label: t("a11y.font.atkinson"), help: t("a11y.font.atkinson.help") },
    { value: "lexend", label: t("a11y.font.lexend"), help: t("a11y.font.lexend.help") },
    { value: "opendyslexic", label: t("a11y.font.opendyslexic"), help: t("a11y.font.opendyslexic.help") },
    { value: "system", label: t("a11y.font.system"), help: t("a11y.font.system.help") },
  ];
  return (
    <Dialog
      title={t("a11y.title")}
      onClose={onClose}
      labelledBy="a11y-title"
      class="a11y-dialog"
      footer={
        <div class="row between">
          <Button variant="ghost" onClick={resetPrefs}>
            {t("a11y.reset")}
          </Button>
          <Button onClick={onClose}>{t("action.done")}</Button>
        </div>
      }
    >
      <div class="stack a11y-stack">
        <div class="a11y-top">
          <p class="muted small">{t("a11y.intro")}</p>
          <LanguageSelect compact />
        </div>
        <div class="a11y-columns">
          <div class="stack stack-sm">
            <p class="a11y-group-title">{t("a11y.group.text")}</p>
            <div class="field">
              <div class="label">{t("a11y.font")}</div>
              <div class="choice-list choice-grid" role="radiogroup" aria-label={t("a11y.font")}>
                {fonts.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    role="radio"
                    class="choice"
                    aria-checked={prefs.font === f.value}
                    onClick={() => setPrefs({ font: f.value })}
                    data-font={f.value}
                  >
                    <div>
                      <div class="title">{f.label}</div>
                      <div class="sub">{f.help}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <Segmented<SizeChoice>
              label={t("a11y.size")}
              value={prefs.size}
              onChange={(size) => setPrefs({ size })}
              options={[
                { value: "default", label: t("a11y.size.default") },
                { value: "large", label: t("a11y.size.large") },
                { value: "xl", label: t("a11y.size.xl") },
              ]}
            />
            <Segmented<SpacingChoice>
              label={t("a11y.spacing")}
              value={prefs.spacing}
              onChange={(spacing) => setPrefs({ spacing })}
              options={[
                { value: "default", label: t("a11y.spacing.default") },
                { value: "comfortable", label: t("a11y.spacing.comfortable") },
                { value: "reading", label: t("a11y.spacing.reading") },
              ]}
            />
            <p class="muted xs help-line" title={t("a11y.spacing.reading.help")}>
              {t("a11y.spacing.reading.help")}
            </p>
          </div>
          <div class="stack stack-sm">
            <p class="a11y-group-title">{t("a11y.group.colour")}</p>
            {/* Four options in a narrow column: a forced 2x2 is shorter than auto-fit's uneven
             * 3-then-1 wrap, which is what this class hook buys without a Segmented prop. */}
            <div class="a11y-2col">
              <Segmented<ThemeChoice>
                label={t("a11y.theme")}
                value={prefs.theme}
                onChange={(theme) => setPrefs({ theme })}
                options={[
                  { value: "auto", label: t("a11y.theme.auto") },
                  { value: "light", label: t("a11y.theme.light") },
                  { value: "dark", label: t("a11y.theme.dark") },
                  { value: "cream", label: t("a11y.theme.cream") },
                ]}
              />
            </div>
            <Segmented<ContrastChoice>
              label={t("a11y.contrast")}
              value={prefs.contrast}
              onChange={(contrast) => setPrefs({ contrast })}
              options={[
                { value: "normal", label: t("a11y.contrast.normal") },
                { value: "high", label: t("a11y.contrast.high") },
              ]}
            />
            <Segmented<MotionChoice>
              label={t("a11y.motion")}
              value={prefs.motion}
              onChange={(motion) => setPrefs({ motion })}
              options={[
                { value: "auto", label: t("a11y.motion.auto") },
                { value: "reduced", label: t("a11y.motion.reduced") },
                { value: "full", label: t("a11y.motion.full") },
              ]}
            />
          </div>
        </div>
        <div class="card sunken a11y-preview" aria-live="polite">
          <p class="a11y-preview-heading">{t("a11y.title")}</p>
          <p class="a11y-preview-body">{t("a11y.preview")}</p>
        </div>
      </div>
    </Dialog>
  );
}
